import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { useToast } from "@shared/hooks/use-toast";
import { Tabs } from "@shared/ui/tabs";
import { Clock, FileEdit, FileText, Tag } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { sopFilters } from "../../types";

// Import all the new components
import { SopFilterPanel } from "../filter/SopFilterPanel";
import MasterPageHeader from "../header/MasterPageHeader";
import SopTemplatesTable from "../table/SopTemplateTable";

// Import the API hooks
import {
  useCloneSopTemplate,
  useServiceProcedureSummary,
  useSOPTemplates,
  useToggleTemplateStatus,
} from "../../hooks";

const MasterPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Track current active tab for namespace handling
  const [currentTab, setCurrentTab] = useState("templates");

  // API hooks - now using server-side filtering
  const {
    data: summaryData,
    isLoading: isSummaryLoading,
    error: summaryError,
  } = useServiceProcedureSummary();
  const cloneMutation = useCloneSopTemplate();

  const handleCloneTemplate = async (template: any) => {
    try {
      await cloneMutation.mutateAsync(template.id);
    } catch (error) {
      // Error is already handled in the mutation's onError
      console.error("Clone error:", error);
    }
  };

  // Get active templates (is_draft = false) with server-side filtering
  const {
    templates: activeTemplates,
    isLoading: isActiveLoading,
    error: activeError,
    pagination: activePagination,
    currentFilters: activeFilters,
    handleSearch: handleActiveSearch,
    handlePageChange: handleActivePageChange,
    refetch: refetchActive,
  } = useSOPTemplates(false, "active");

  // Get draft templates (is_draft = true) with server-side filtering
  const {
    templates: draftTemplates,
    isLoading: isDraftLoading,
    pagination: draftPagination,
    currentFilters: draftFilters,
    handleSearch: handleDraftSearch,
    handlePageChange: handleDraftPageChange,
    refetch: refetchDraft,
  } = useSOPTemplates(true, "draft");

  // Toggle status mutation
  const toggleStatusMutation = useToggleTemplateStatus();

  useEffect(() => {
    refetchDraft(); // Just refetch the current query
  }, [location.pathname]);

  useEffect(() => {
    refetchActive(); // Just refetch the current query
  }, [location.pathname]);
  // Helper function to get namespaced parameter
  const getNamespacedParam = useCallback(
    (key: string, namespace: string) => {
      return searchParams.get(namespace ? `${namespace}_${key}` : key);
    },
    [searchParams]
  );

  // Parse current filter values from URL based on current tab
  const getCurrentTabFilters = useMemo(() => {
    const namespace = currentTab === "templates" ? "active" : "draft";

    return {
      searchQuery: getNamespacedParam("query", namespace) || "",
      utilityTypeFilter: getNamespacedParam("utilityType", namespace) || "all",
      priorityFilter: getNamespacedParam("priority", namespace) || "all",
      statusFilter: getNamespacedParam("status", namespace) || "all",
    };
  }, [currentTab, getNamespacedParam]);

  const { searchQuery, utilityTypeFilter, priorityFilter, statusFilter } =
    getCurrentTabFilters;

  // KPI Cards configuration using QuickStatsCard
  const sopKpiCards = useMemo(() => {
    const summary = summaryData?.result;

    return [
      {
        title: "Total SOPs",
        count: isSummaryLoading ? "..." : summary?.totalTemplateCount ?? 0,
        trendSummary: "Standard Operating Procedures across all templates",
        trendDirection: "up" as const,
        icon: <FileText className="h-8 w-8 text-blue-500" />,
      },
      {
        title: "Active SOPs",
        count: isSummaryLoading ? "..." : summary?.activeTemplateCount ?? 0,
        trendSummary: "SOPs currently in use and operational",
        trendDirection: "up" as const,
        icon: <Clock className="h-8 w-8 text-orange-500" />,
      },
      {
        title: "Template Types",
        count: isSummaryLoading ? "..." : summary?.typeCount ?? 0,
        trendSummary: "Unique categories of SOP templates",
        trendDirection: "up" as const,
        icon: <Tag className="h-8 w-8 text-green-500" />,
      },
      {
        title: "Avg Rules Duration",
        count: isSummaryLoading
          ? "..."
          : summary?.avgRulesDuration
          ? `${summary.avgRulesDuration}h`
          : "N/A",
        trendSummary: "Average processing time for rules execution",
        trendDirection: "down" as const,
        icon: <Clock className="h-8 w-8 text-red-500" />,
      },
    ];
  }, [summaryData, isSummaryLoading]);

  // Updated filter application logic with namespace support
  const handleApplyFilters = useCallback(
    (newFilters: Partial<sopFilters>) => {
      const params = new URLSearchParams(searchParams);
      const namespace = currentTab === "templates" ? "active" : "draft";

      // Clear namespaced params
      ["status", "utilityType", "priority", "page"].forEach((key) => {
        const namespacedKey = namespace ? `${namespace}_${key}` : key;
        params.delete(namespacedKey);
      });

      // Set namespaced params
      if (newFilters.status && newFilters.status !== "all") {
        const key = namespace ? `${namespace}_status` : "status";
        params.set(key, newFilters.status);
      }

      if (newFilters.utilityType && newFilters.utilityType !== "all") {
        const key = namespace ? `${namespace}_utilityType` : "utilityType";
        params.set(key, newFilters.utilityType);
      }

      if (newFilters.priority && newFilters.priority !== "all") {
        const key = namespace ? `${namespace}_priority` : "priority";
        params.set(key, newFilters.priority);
      }

      // Reset to page 1 when applying filters
      const pageKey = namespace ? `${namespace}_page` : "page";
      params.set(pageKey, "1");

      setSearchParams(params);
    },
    [searchParams, setSearchParams, currentTab]
  );

  // Updated reset filters logic with namespace support
  const handleResetFilters = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    const namespace = currentTab === "templates" ? "active" : "draft";

    // Remove all namespaced filter params
    ["status", "utilityType", "priority", "page", "query"].forEach((key) => {
      const namespacedKey = namespace ? `${namespace}_${key}` : key;
      params.delete(namespacedKey);
    });

    setSearchParams(params);

    toast({
      title: "Filters Cleared",
      description: "All filters have been reset.",
      duration: 3000,
    });
  }, [searchParams, setSearchParams, currentTab, toast]);

  // Updated search change handler with namespace support
  const handleSearchChange = useCallback(
    (searchTerm: string, paramName: string = "query") => {
      const params = new URLSearchParams(searchParams);
      const namespace = currentTab === "templates" ? "active" : "draft";
      const namespacedParamName = namespace
        ? `${namespace}_${paramName}`
        : paramName;

      if (searchTerm.trim()) {
        params.set(namespacedParamName, searchTerm);
      } else {
        params.delete(namespacedParamName);
      }

      // Reset to page 1 when searching
      const pageKey = namespace ? `${namespace}_page` : "page";
      params.set(pageKey, "1");

      setSearchParams(params);
    },
    [searchParams, setSearchParams, currentTab]
  );

  // Tab change handler to update current tab state
  const handleTabChange = useCallback((tabValue: string) => {
    setCurrentTab(tabValue);
  }, []);

  // Event handlers
  const handleCreateSop = () => {
    navigate("/service-orders/wx-master/create");
  };

  const handleBulkUpload = () => {
    navigate("/bulk-upload");
  };

  const handleViewDetails = (templateId: string, templateName: string) => {
    toast({
      title: "Viewing SOP Details",
      description: `Loading ${templateName} details page`,
      duration: 3000,
    });
    navigate(`/service-orders/wx-master/details/${templateId}`);
  };

  const handleEditTemplate = (template: any) => {
    toast({
      title: "Editing SOP",
      description: `Loading ${template.name} in edit mode`,
      duration: 3000,
    });
    navigate(
      `/service-orders/wx-master/create?mode=edit&id=${template.id}`
    );
  };

  const handleStatusToggle = async (templateId: string, isActive: boolean) => {
    try {
      await toggleStatusMutation.mutateAsync({
        templateId: templateId,
        isActive: isActive,
      });

      refetchActive();
      refetchDraft();

      toast({
        title: "Status Updated",
        description: `Template ${templateId} status has been updated.`,
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update template status. Please try again.",
        variant: "destructive",
        duration: 3000,
      });
    }
  };

  // Create current filters object for filterPanel based on current tab
  const currentFilters: Partial<sopFilters> = useMemo(
    () => ({
      status: statusFilter !== "all" ? statusFilter : undefined,
      utilityType: utilityTypeFilter !== "all" ? utilityTypeFilter : undefined,
      priority: priorityFilter !== "all" ? priorityFilter : undefined,
      search_data: searchQuery || undefined,
    }),
    [statusFilter, utilityTypeFilter, priorityFilter, searchQuery]
  );

  // Create tab components using real API data
  const tabComponents = useMemo(
    () => ({
      templates: {
        label: "SOP Templates",
        icon: <FileText className="h-4 w-4" />,
        count: activePagination?.totalItems || 0,
        component: (
          <div className="mt-4">
            <SopTemplatesTable
              templates={activeTemplates}
              isDraftsView={false}
              onViewDetails={handleViewDetails}
              onEditTemplate={handleEditTemplate}
              onStatusToggle={(templateId, isActive) =>
                handleStatusToggle(templateId, isActive)
              }
              loading={isActiveLoading}
              onClone={handleCloneTemplate}
              pagination={activePagination}
              onPageChange={handleActivePageChange}
              emptyMessage="No active templates found matching your search and filters."
              // Search integration like UserTable
              onSearch={handleSearchChange}
              searchPlaceholder="Search SOP templates..."
              searchParamName="query"
              searchDebounceMs={300}
              // Filter panel integration like UserTable
              currentFilters={currentFilters}
              onApplyFilters={handleApplyFilters}
              onResetFilters={handleResetFilters}
              filterPanel={
                <SopFilterPanel
                  currentFilters={currentFilters}
                  onApplyFilters={handleApplyFilters}
                  onResetFilters={handleResetFilters}
                />
              }
            />
          </div>
        ),
      },
      drafts: {
        label: "Draft Templates",
        icon: <FileEdit className="h-4 w-4" />,
        count: draftPagination?.totalItems || 0,
        countClassName: "bg-primary",
        component: (
          <div className="mt-4">
            <SopTemplatesTable
              templates={draftTemplates}
              isDraftsView={true}
              onViewDetails={handleViewDetails}
              onEditTemplate={handleEditTemplate}
              onStatusToggle={(templateId) => handleStatusToggle(templateId)}
              loading={isDraftLoading}
              onClone={handleCloneTemplate}
              pagination={draftPagination}
              onPageChange={handleDraftPageChange}
              emptyMessage="No draft templates found matching your search and filters."
              // Search integration like UserTable
              onSearch={handleSearchChange}
              searchPlaceholder="Search draft templates..."
              searchParamName="query"
              searchDebounceMs={300}
              // Filter panel integration like UserTable
              currentFilters={currentFilters}
              onApplyFilters={handleApplyFilters}
              onResetFilters={handleResetFilters}
              filterPanel={
                <SopFilterPanel
                  currentFilters={currentFilters}
                  onApplyFilters={handleApplyFilters}
                  onResetFilters={handleResetFilters}
                />
              }
            />
          </div>
        ),
      },
    }),
    [
      activeTemplates,
      draftTemplates,
      isActiveLoading,
      isDraftLoading,
      activePagination,
      draftPagination,
      handleCloneTemplate,
      handleActivePageChange,
      handleDraftPageChange,
      handleViewDetails,
      handleEditTemplate,
      handleStatusToggle,
      handleSearchChange,
      currentFilters,
      handleApplyFilters,
      handleResetFilters,
    ]
  );

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <MasterPageHeader
        onCreateSopClick={handleCreateSop}
        onBulkUploadClick={handleBulkUpload}
      />

      {/* Loading indicator for clone operation */}
      {cloneMutation.isPending && (
        <div className="fixed bottom-4 right-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg shadow-lg">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            Cloning template...
          </div>
        </div>
      )}

      {/* KPI Cards - Using loop with QuickStatsCard */}
      {isSummaryLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, index) => (
            <div
              key={index}
              className="bg-white rounded-lg p-6 shadow-sm animate-pulse"
            >
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-full"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {sopKpiCards.map((card, index) => (
            <QuickStatsCard
              key={index}
              title={card.title}
              count={card.count}
              trendSummary={card.trendSummary}
              trendDirection={card.trendDirection}
              icon={card.icon}
            />
          ))}
        </div>
      )}

      {/* Tabs with integrated filtering - filters now appear between tabs and table content */}
      <Tabs
        className="w-full"
        defaultValue="templates"
        urlMapping={{
          templates: "templates",
          drafts: "drafts",
        }}
        idPrefix="sop"
        tabComponents={tabComponents}
        tabsListClassName="grid w-full grid-cols-2"
        onTabChange={handleTabChange} // Add tab change handler
      />

      {/* Loading indicator for status toggle */}
      {toggleStatusMutation.isPending && (
        <div className="fixed bottom-4 right-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg shadow-lg">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            Updating template status...
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterPage;
